import type { HelpContent } from './types';

export const settingsIntegrationsHelp: HelpContent = {
  pageTitle: 'Integrations',
  description: 'The Integrations page is where administrators connect and manage third-party systems — such as accounting platforms — that sync data with this application.',

  functionality: {
    overview:
      'This page lists all available integrations and shows the current connection status for each. Once an integration is connected, data such as company details, nominal codes, and financial transactions can be synchronised automatically.',
    keyFeatures: [
      {
        label: 'Finance system integration',
        detail: 'Connect to an accounting platform to sync company details, nominal codes, and financial data.',
        requiredPermission: 'settings.integrations',
      },
      {
        label: 'Connection status indicators',
        detail: 'See at a glance which integrations are connected, disconnected, or have errors.',
        requiredPermission: 'settings.integrations',
      },
      {
        label: 'Connect and disconnect',
        detail: 'Authorise a new integration or revoke access to an existing one.',
        requiredPermission: 'settings.integrations',
      },
      {
        label: 'Sync configuration',
        detail: 'Configure which data flows are active and how frequently data is synchronised.',
        requiredPermission: 'settings.integrations',
      },
    ],
    sectionsAndFields: [
      {
        name: 'Integration cards',
        description: 'Each card shows an integration\'s name, logo, description, current status, and connect/disconnect actions.',
        requiredPermission: 'settings.integrations',
      },
      {
        name: 'Status indicator',
        description: 'Shows whether the integration is Connected, Disconnected, or has an error that needs attention.',
        requiredPermission: 'settings.integrations',
      },
      {
        name: 'Connect button',
        description: 'Opens the authorisation flow for the selected integration.',
        requiredPermission: 'settings.integrations',
      },
      {
        name: 'Disconnect button',
        description: 'Revokes the integration\'s access and stops all data synchronisation.',
        requiredPermission: 'settings.integrations',
      },
    ],
    useCases: [
      'An administrator connects the company\'s Xero account to automatically sync company details and nominal codes.',
      'A finance lead disconnects an integration that is no longer in use to prevent stale data from being synced.',
      'An administrator reviews the sync status to confirm that nominal code updates have been imported successfully.',
    ],
  },

  userGuide: {
    gettingStarted:
      'Before connecting an integration, ensure you have the required administrator credentials for the third-party system. The connection process will redirect you to that system\'s authorisation page.',
    steps: [
      {
        title: 'How do I connect a finance integration?',
        requiredPermission: 'settings.integrations',
        items: [
          { text: 'Find the integration card for your accounting platform.' },
          { text: 'Click Connect.' },
          {
            text: 'You will be redirected to the third-party authorisation page. Log in with your administrator credentials for that system and approve the connection.',
            note: 'You must have administrator access in the third-party system to authorise the connection.',
          },
          { text: 'Once authorised, you are returned to this page and the status indicator updates to Connected.' },
          {
            text: 'Company details, nominal codes, and other synced data will begin importing shortly.',
            tip: 'After connecting, check General Settings to confirm that company details have been imported correctly.',
          },
        ],
      },
      {
        title: 'How do I disconnect an integration?',
        requiredPermission: 'settings.integrations',
        items: [
          { text: 'Find the connected integration card.' },
          { text: 'Click Disconnect.' },
          {
            text: 'Confirm the disconnection in the dialog.',
            note: 'Disconnecting stops all future data synchronisation. Existing synced data remains in the system but will no longer be updated automatically. Fields that were read-only due to the sync (such as company details) become editable again.',
          },
        ],
      },
    ],
    proTips: [
      'Connect your finance integration before setting up nominal code mappings in Project Settings — the integration imports the nominal codes automatically.',
      'If the status shows an error, try disconnecting and reconnecting. Token expiry or permission changes in the third-party system are the most common causes.',
      'Disconnecting an integration does not delete any previously synced data — it only stops future synchronisation.',
    ],
  },

  faq: [
    {
      question: 'What happens to my data if I disconnect an integration?',
      answer:
        'Existing synced data (such as company details and nominal codes) remains in the system. Synchronisation stops, and fields that were read-only due to the sync become editable again.',
      requiredPermission: 'settings.integrations',
    },
    {
      question: 'Why are my company details read-only in General Settings?',
      answer:
        'When a finance integration is connected and syncing company details, those fields are managed by the integration and cannot be edited here. Disconnect the integration or edit the details in your finance system.',
    },
    {
      question: 'How do I know if my integration is working correctly?',
      answer:
        'The status indicator on the integration card shows Connected when active. If there is an error, the indicator turns red and a description of the issue is shown. Contact support if the error persists after reconnecting.',
      requiredPermission: 'settings.integrations',
    },
    {
      question: 'Can I connect more than one finance integration at the same time?',
      answer:
        'This depends on your account configuration. Most accounts support one primary finance integration. Contact support if you need to connect multiple systems.',
      requiredPermission: 'settings.integrations',
    },
  ],

  relatedPages: [
    { label: 'General Settings', path: '/settings/general' },
    { label: 'Project Settings', path: '/settings/project' },
    { label: 'Settings Hub', path: '/settings' },
  ],
};
